import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PLANBASE_CONFIG } from '../config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { ImageUploadZone } from '../components/ImageUploadZone';
import { getUserAuthHeaders, decodeJWT } from '../utils/auth';
import { Checkbox } from '../components/ui/checkbox';
import { MobilePageHeader } from '../components/MobilePageHeader';
import { useHideBottomNav } from '../hooks/useHideBottomNav';
import { resolveUserTier } from '../lib/plan';
import { usePlatform } from '../hooks/usePlatform';

import { ArrowDownTrayIcon, ArrowPathIcon, ArrowUpTrayIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface ProjectImage { url: string; title: string; description: string; }

interface ProjectFormData {
  title: string;
  description: string;
  category: string;
  style: string;
  location: string;
  year: string;
  architect: string;
  coverImage: string;
  images: ProjectImage[];
  tags: string[];
  allowPublishBeyondPlanbase: boolean;
  isLocked: boolean;
  isOriginalOwner: boolean;
}

const DRAFT_KEY = 'planbase_upload_draft';

const empty: ProjectFormData = {
  title: '', description: '', category: '', style: '',
  location: '', year: '', architect: '', coverImage: '',
  images: [], tags: [], allowPublishBeyondPlanbase: false,
  isLocked: false, isOriginalOwner: false,
};

function loadDraft(): ProjectFormData {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) return { ...empty, ...JSON.parse(saved) };
  } catch {}
  return empty;
}

function saveDraft(data: ProjectFormData) {
  localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
}

function clearDraft() {
  localStorage.removeItem(DRAFT_KEY);
}

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({
  label, required, error, children,
}: { label: string; required?: boolean; error?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">
        {label}{required && <span className="text-primary ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

const inputCls = (err?: string) =>
  `w-full h-12 md:h-9 px-3 border rounded-xl text-sm bg-background focus:outline-none transition-colors ${
    err ? 'border-destructive focus:border-destructive' : 'border-input focus:border-primary'
  }`;

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function UploadProject() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile } = usePlatform();
  useHideBottomNav();

  const userPlan = resolveUserTier(user);
  const maxImages = userPlan === 'enterprise'
    ? PLANBASE_CONFIG.planLimits.enterprise.maxImagesPerProject
    : userPlan === 'pro'
      ? PLANBASE_CONFIG.planLimits.pro.maxImagesPerProject
      : PLANBASE_CONFIG.planLimits.free.maxImagesPerProject;

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [touched, setTouched] = useState<Partial<Record<keyof ProjectFormData, boolean>>>({});
  const [formData, setFormData] = useState<ProjectFormData>(loadDraft);
  const [searchParams, setSearchParams] = useSearchParams();
  // Tracks which fields were prefilled from the PDF import so we can
  // show a subtle "auto-filled" chip until the user edits them.
  const [pdfSourcedFields, setPdfSourcedFields] = useState<Set<keyof ProjectFormData>>(new Set());
  const [pdfMeta, setPdfMeta] = useState<{ fileName?: string; pageCount?: number } | null>(null);

  // Hydrate from the /portfolio/new PDF import handoff. Reads once,
  // then removes the sessionStorage entry so a refresh doesn't
  // re-prefill on top of user edits.
  useEffect(() => {
    if (searchParams.get('source') !== 'pdf') return;
    try {
      const raw = sessionStorage.getItem('planbase_pdf_import');
      if (!raw) return;
      const payload = JSON.parse(raw) as {
        fileName?: string;
        pageCount?: number;
        fields?: Partial<ProjectFormData>;
        images?: string[];
        coverSuggestion?: string;
      };
      const fields = payload.fields || {};
      const extractedImages = Array.isArray(payload.images) ? payload.images.filter(Boolean) : [];
      const touchedKeys: (keyof ProjectFormData)[] = [];
      setFormData((prev) => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(fields)) {
          if (v === undefined || v === null || v === '') continue;
          if (Array.isArray(v) && v.length === 0) continue;
          (next as any)[k] = v;
          touchedKeys.push(k as keyof ProjectFormData);
        }
        // Attach extracted images. First one becomes the cover (unless the
        // LLM's coverSuggestion says otherwise), the rest fill the gallery
        // up to the tier's maxImages cap.
        if (extractedImages.length > 0) {
          const cover = payload.coverSuggestion || extractedImages[0];
          if (!next.coverImage) {
            next.coverImage = cover;
            touchedKeys.push('coverImage');
          }
          const galleryPool = extractedImages.filter((u) => u !== next.coverImage);
          if (galleryPool.length > 0 && (!next.images || next.images.length === 0)) {
            next.images = galleryPool.slice(0, 20).map((url) => ({ url, title: '', description: '' }));
            touchedKeys.push('images');
          }
        }
        return next;
      });
      setPdfSourcedFields(new Set(touchedKeys));
      setPdfMeta({ fileName: payload.fileName, pageCount: payload.pageCount });
      sessionStorage.removeItem('planbase_pdf_import');
      // Clean the ?source= off the URL so a refresh doesn't retry.
      searchParams.delete('source');
      setSearchParams(searchParams, { replace: true });
      const filledFields = touchedKeys.filter((k) => k !== 'coverImage' && k !== 'images').length;
      const parts: string[] = [];
      if (filledFields) parts.push(`${filledFields} field${filledFields === 1 ? '' : 's'}`);
      if (extractedImages.length) parts.push(`${extractedImages.length} image${extractedImages.length === 1 ? '' : 's'}`);
      toast.success(
        parts.length
          ? `Prefilled ${parts.join(' and ')} from your PDF.`
          : "PDF imported — Orbi couldn't structure the content, so nothing was prefilled.",
      );
    } catch {
      /* ignore malformed handoff */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save draft whenever form changes; user edit removes the
  // "auto-filled" chip for that field.
  useEffect(() => {
    saveDraft(formData);
  }, [formData]);

  const update = (field: keyof ProjectFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));
    // Any user edit invalidates the "auto-filled" chip for that field.
    setPdfSourcedFields((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };
  const set = (field: keyof ProjectFormData) => (val: any) => update(field, val);

  // Validation
  const errors: Partial<Record<keyof ProjectFormData, string>> = {};
  if (touched.title && !formData.title.trim()) errors.title = 'Title is required';
  if (touched.description && !formData.description.trim()) errors.description = 'Description is required';
  if (touched.category && !formData.category) errors.category = 'Please select a category';
  if (touched.coverImage && !formData.coverImage) errors.coverImage = 'Cover image is required';

  const validateAll = () => {
    const allTouched: Partial<Record<keyof ProjectFormData, boolean>> = {
      title: true, description: true, category: true, coverImage: true,
    };
    setTouched(prev => ({ ...prev, ...allTouched }));
    return !!(
      formData.title.trim() &&
      formData.description.trim() &&
      formData.category &&
      formData.coverImage
    );
  };

  const handleSaveDraft = () => {
    saveDraft(formData);
    setDraftSaved(true);
    setTimeout(() => setDraftSaved(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAll()) {
      setSubmitError('Please fill in all required fields.');
      return;
    }
    if (!formData.isOriginalOwner) {
      setSubmitError('Please confirm you are the original owner of this work.');
      return;
    }
    setLoading(true);
    setSubmitError(null);
    try {
      const token = localStorage.getItem('planbase_token');
      if (!token) throw new Error('Please log in to upload projects');
      const payload = decodeJWT(token);
      const userId = payload?.sub || payload?.user_id || payload?.id;
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/cms/records`, {
        method: 'POST',
        headers: { ...getUserAuthHeaders(), 'X-User-ID': userId },
        body: JSON.stringify({
          database_slug: 'projects',
          data: formData,
          status: 'pending',
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || 'Failed to upload project');
      }
      clearDraft();
      setSuccess(true);
      setTimeout(() => navigate('/portfolio'), 2000);
    } catch (err: any) {
      setSubmitError(err.message || 'An error occurred while uploading');
    } finally {
      setLoading(false);
    }
  };

  const removeImage = (index: number) =>
    update('images', formData.images.filter((_, i) => i !== index));

  const updateImageDetails = (index: number, field: 'title' | 'description', value: string) =>
    update('images', formData.images.map((img, i) => i === index ? { ...img, [field]: value } : img));

  const addTag = (value: string) => {
    const t = value.trim();
    if (t && !formData.tags.includes(t)) update('tags', [...formData.tags, t]);
  };

  const handleTagInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.includes(',')) {
      value.split(',').map(t => t.trim()).filter(Boolean).forEach(addTag);
      setNewTag('');
    } else setNewTag(value);
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newTag.trim()) { e.preventDefault(); addTag(newTag); setNewTag(''); }
    else if (e.key === 'Backspace' && !newTag && formData.tags.length > 0)
      update('tags', formData.tags.slice(0, -1));
  };

  if (success) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center px-4">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/40 mb-4">
          <CheckCircleIcon className="h-7 w-7 text-green-600" />
        </div>
        <h3 className="text-base font-semibold text-foreground mb-1">Project Submitted!</h3>
        <p className="text-sm text-muted-foreground">Your project is pending review. Redirecting...</p>
      </div>
    );
  }

  // On mobile: flat sections separated by dividers, no card border
  // On desktop: classic white card with divide-y
  const wrapperCls = isMobile
    ? 'space-y-0 divide-y divide-border'
    : 'bg-white dark:bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border';

  const sectionCls = isMobile ? 'py-4 space-y-1.5' : 'px-4 py-3 space-y-1.5';

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-6">
      {/* Mobile header */}
      <MobilePageHeader
        title="Upload Project"
        right={
          <button
            onClick={handleSaveDraft}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border/50 bg-white/10 dark:bg-white/5 backdrop-blur-sm text-xs text-foreground active:scale-95 transition-transform"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            {draftSaved ? 'Saved!' : 'Save'}
          </button>
        }
      />

      {/* Desktop header */}
      <div className="hidden md:flex items-center justify-between">
        <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Upload Project</h1>
        <div className="flex items-center gap-3">
          <button onClick={handleSaveDraft}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            {draftSaved ? 'Draft saved!' : 'Save draft'}
          </button>
          <button type="button" onClick={() => navigate('/portfolio')}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            Cancel
          </button>
        </div>
      </div>

      {/* Draft restored notice */}
      {formData.title && (
        <div className="flex items-center justify-between bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900 rounded-xl px-3 py-2">
          <p className="text-xs text-orange-700 dark:text-orange-400">Draft restored — your last changes are saved locally.</p>
          <button onClick={() => { setFormData(empty); clearDraft(); }}
            className="text-xs text-primary underline ml-2">Clear</button>
        </div>
      )}

      {submitError && (
        <div className="border border-destructive/40 bg-destructive/10 rounded-xl px-4 py-3">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>
      )}

      {/*
        PDF-import provenance banner. Named as auto-fill, not as a
        guarantee: chips disappear the moment the user edits a field
        (see update() above), so the count reflects the fields that
        still hold PDF-sourced values.
      */}
      {pdfMeta && pdfSourcedFields.size > 0 && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 px-4 py-3">
          <p className="text-sm text-foreground">
            <span className="font-medium">Prefilled from PDF</span>
            {pdfMeta.fileName ? <> — <span className="text-muted-foreground">{pdfMeta.fileName}</span></> : null}
            {typeof pdfMeta.pageCount === 'number' ? <> · {pdfMeta.pageCount} pages</> : null}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {pdfSourcedFields.size} field{pdfSourcedFields.size === 1 ? '' : 's'} still hold values Orbi extracted — review them before publishing.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={wrapperCls}>

          {/* Title */}
          <div className={sectionCls}>
            <Field label="Title" required error={errors.title}>
              <input type="text" value={formData.title}
                onChange={e => set('title')(e.target.value)}
                onBlur={() => setTouched(p => ({ ...p, title: true }))}
                className={inputCls(errors.title)} placeholder="Modern Villa Design" />
            </Field>
          </div>

          {/* Description */}
          <div className={sectionCls}>
            <Field label="Description" required error={errors.description}>
              <textarea value={formData.description}
                onChange={e => set('description')(e.target.value)}
                onBlur={() => setTouched(p => ({ ...p, description: true }))}
                rows={4}
                className={`w-full px-3 py-2.5 border rounded-xl text-sm bg-background focus:outline-none resize-y min-h-[100px] max-h-[240px] scrollbar-hide transition-colors ${
                  errors.description ? 'border-destructive' : 'border-input focus:border-primary'
                }`}
                placeholder="Describe your project, concept, materials and design intent..." />
            </Field>
          </div>

          {/* Category + Style */}
          <div className={sectionCls}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category" required error={errors.category}>
                <Select value={formData.category} onValueChange={v => { set('category')(v); setTouched(p => ({ ...p, category: true })); }}>
                  <SelectTrigger className={`h-12 md:h-9 rounded-xl ${errors.category ? 'border-destructive' : ''}`}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PLANBASE_CONFIG.categories.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Style">
                <Select value={formData.style} onValueChange={set('style')}>
                  <SelectTrigger className="h-12 md:h-9 rounded-xl"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {PLANBASE_CONFIG.styles.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>

          {/* Location + Year + Architect */}
          <div className={sectionCls}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Location">
                <input type="text" value={formData.location}
                  onChange={e => set('location')(e.target.value)}
                  className={inputCls()} placeholder="Lagos, Nigeria" />
              </Field>
              <Field label="Year">
                <input type="text" value={formData.year}
                  onChange={e => set('year')(e.target.value)}
                  className={inputCls()} placeholder="2024" />
              </Field>
              <Field label="Architect">
                <input type="text" value={formData.architect}
                  onChange={e => set('architect')(e.target.value)}
                  className={inputCls()} placeholder="Your name" />
              </Field>
            </div>
          </div>

          {/* Tags */}
          <div className={sectionCls}>
            <Field label="Tags">
              <div className="border border-input rounded-xl px-3 py-2.5 bg-background focus-within:border-primary flex flex-wrap gap-1.5 items-center min-h-[48px] md:min-h-[36px] transition-colors">
                {formData.tags.map((tag, i) => (
                  <span key={i} className="flex items-center gap-1 px-2 py-0.5 bg-muted rounded-full text-xs text-foreground">
                    {tag}
                    <button type="button" onClick={() => update('tags', formData.tags.filter((_, j) => j !== i))}>
                      <XMarkIcon className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
                <input type="text" value={newTag}
                  onChange={handleTagInput} onKeyDown={handleTagKeyDown}
                  className="flex-1 min-w-[100px] text-sm bg-transparent focus:outline-none"
                  placeholder={formData.tags.length === 0 ? 'Add tags (comma or Enter)...' : ''} />
              </div>
            </Field>
          </div>

          {/* Cover Image */}
          <div className={sectionCls}>
            <Field label="Cover Image" required error={errors.coverImage}>
              {formData.coverImage ? (
                <div className="relative rounded-xl overflow-hidden border border-border group w-full max-w-xs">
                  <img src={formData.coverImage} alt="Cover" className="w-full h-48 md:h-40 object-cover" />
                  <button type="button" onClick={() => { set('coverImage')(''); setTouched(p => ({ ...p, coverImage: true })); }}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 text-white rounded-full">
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div onClick={() => setTouched(p => ({ ...p, coverImage: true }))}>
                  <ImageUploadZone label="" required onImageUploaded={url => { set('coverImage')(url); setTouched(p => ({ ...p, coverImage: true })); }} />
                </div>
              )}
            </Field>
          </div>

          {/* Additional Images */}
          <div className={sectionCls}>
            <Field label={`Additional Images (${formData.images.length}/${maxImages})`}>
              <div className="space-y-3">
                {formData.images.length < maxImages && (
                  <ImageUploadZone label="" maxFiles={maxImages} currentCount={formData.images.length}
                    onImageUploaded={url => {
                      if (formData.images.length < maxImages)
                        update('images', [...formData.images, { url, title: '', description: '' }]);
                    }} />
                )}
                {userPlan === 'free' && maxImages <= 10 && (
                  <p className="text-[11px] text-primary">Free plan: {maxImages} images max. Upgrade for more.</p>
                )}
                {formData.images.map((image, index) => (
                  <div key={index} className="flex gap-3 border border-border rounded-xl p-3">
                    <div className="relative flex-shrink-0">
                      <img src={image.url} alt={image.title || `Image ${index + 1}`} className="w-20 h-20 object-cover rounded-lg" />
                      <button type="button" onClick={() => removeImage(index)}
                        className="absolute -top-1.5 -right-1.5 p-1 bg-black/60 text-white rounded-full">
                        <XMarkIcon className="h-2.5 w-2.5" />
                      </button>
                    </div>
                    <div className="flex-1 space-y-2">
                      <input type="text" value={image.title}
                        onChange={e => updateImageDetails(index, 'title', e.target.value)}
                        className="w-full h-10 md:h-8 px-3 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary"
                        placeholder="Drawing type (e.g. Front Elevation)" />
                      <textarea value={image.description}
                        onChange={e => updateImageDetails(index, 'description', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-1.5 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary resize-none"
                        placeholder="Brief description..." />
                    </div>
                  </div>
                ))}
              </div>
            </Field>
          </div>

          {/* Permissions */}
          <div className={`${sectionCls} space-y-4`}>
            <Checkbox checked={formData.isOriginalOwner}
              onChange={v => set('isOriginalOwner')(v)}
              label="I confirm that I am the original owner of this work and have the rights to publish it. *" />
            <Checkbox checked={formData.allowPublishBeyondPlanbase}
              onChange={v => set('allowPublishBeyondPlanbase')(v)}
              label="Allow Planbase to share my project on social media, newsletters, and partner sites" />
            <Checkbox checked={formData.isLocked}
              onChange={v => set('isLocked')(v)}
              label="Lock this project — hide from discovery. Only people with your direct link can view it." />
          </div>

          {/* Footer */}
          <div className={`${isMobile ? 'pt-4' : 'px-4 py-4 bg-muted/40'}`}>
            <button type="submit" disabled={loading}
              className="w-full h-12 md:h-10 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
              {loading ? (
                <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Submitting...</>
              ) : (
                <><ArrowUpTrayIcon className="h-4 w-4" /> Submit for Review</>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
